export interface PendingVerification {
  phone: string;
  method: 'sms' | 'whatsapp';
  verificationId: string;
}

/**
 * A code that has been sent and is waiting to be entered, kept across a reload of the tab it was sent
 * from.
 *
 * `sessionStorage` rather than the URL, because a URL is broadcast: every analytics tag on the page
 * reports `document.location`, so a number in a query param is a number handed to each of them (see
 * `PhoneVerifyModal`). Storage is not read automatically by anything — which is a real reduction, not a
 * guarantee, since a script on the origin still *could* read it. That is why this is scoped as tightly
 * as it is: the tab's session, cleared the moment the flow ends either way.
 */
const KEY = 'symphony-pending-verification';

export function savePendingVerification(pending: PendingVerification): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(pending));
  } catch {
    // A blocked or full store costs refresh-resilience, nothing more — the flow runs from memory.
  }
}

export function clearPendingVerification(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to recover: the value is unreachable either way.
  }
}

/** Undefined unless a well-formed entry is there — a hand-edited or half-written blob reads as absent. */
export function readPendingVerification(): PendingVerification | undefined {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return undefined;
    const { phone, method, verificationId } = parsed as Record<string, unknown>;
    if (typeof phone !== 'string' || !phone) return undefined;
    if (method !== 'sms' && method !== 'whatsapp') return undefined;
    if (typeof verificationId !== 'string') return undefined;
    return { phone, method, verificationId };
  } catch {
    return undefined;
  }
}
